import React, { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  Camera,
  Sparkles,
  ListChecks,
  Shield,
  Zap,
  Clock,
  Target,
  Check,
  CircleCheck as CheckCircle2,
  ArrowRight,
  ChevronRight,
  Users,
  Smile,
  Share2,
  Star,
} from "lucide-react";
import constants from "../constants/constants";

/* ----------------------------- CTA BUTTON ------------------------------ */
const CTAButton = ({
  label,
  onClick,
  variant = "primary",
}: {
  label: string;
  onClick: () => void;
  variant?: "primary" | "secondary" | "small";
}) => {
  const base =
    "inline-flex items-center justify-center gap-2 font-bold rounded-xl transition-all focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 whitespace-nowrap";
  const styles =
    variant === "secondary"
      ? "border border-primary-600 text-primary-700 bg-white hover:bg-red-50 px-5 py-3 text-base"
      : variant === "small"
      ? "bg-primary-600 text-white hover:bg-primary-700 px-5 py-3 text-base"
      : "bg-primary-600 text-white hover:bg-primary-700 px-7 py-4 text-lg shadow-lg";
  return (
    <button onClick={onClick} type="button" className={`${base} ${styles}`}>
      {label}
      <ArrowRight size={18} />
    </button>
  );
};

/* ----------------------------- TESTIMONIAL ------------------------------ */
const testimonials = [
  {
    quote:
      "Me and my friends checked our hairlines together (I got the best result!)",
    name: "James P.",
    extra: "Shared with 4 friends",
    icon: Users,
  },
  {
    quote:
      "Easy to use, sent my results to my mates and everyone’s checking their risk",
    name: "Ollie T.",
    extra: "Shared via WhatsApp",
    icon: Share2,
  },
];

/* -------------------------- STARTSHARE FUNCTION -------------------------- */
function startShare() {
  const shareUrl = "https://whenbald.com/start";
  const shareData = {
    title: "Check your Hair Loss Risk",
    text: "See your hair loss future instantly (free, private, no signup). Try it now:",
    url: shareUrl,
  };

  if (navigator.share) {
    navigator.share(shareData).catch((err) => {
      if (err && err.name !== "AbortError") {
        alert("Share failed. Copying the link instead.");
        copyFallback();
      }
    });
  } else {
    copyFallback();
  }

  function copyFallback() {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(shareUrl).then(() => {
        alert("Link copied! Share it with your friends.");
      });
    } else {
      const textarea = document.createElement("textarea");
      textarea.value = shareUrl;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      textarea.remove();
      alert("Link copied! Share it with your friends.");
    }
  }
}

/* ------------------------------ MAIN PAGE ------------------------------ */
const Home: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    if (user) console.log("Current user ID:", user.id);
  }, [user]);

  useEffect(() => {
    const state = location.state as { showAuth?: boolean } | null;
    if (state?.showAuth) {
      window.dispatchEvent(new CustomEvent("openAuthModal"));
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  // Navigation
  const goToStart = () => navigate("/start");
  const goToFAQ = () => navigate("/faq");

  // Steps (How it works)
  const steps = [
    {
      title: "Upload Your Photo",
      description: "Snap or upload a photo of your hair loss.",
      icon: Camera,
    },
    {
      title: "Instant AI Prediction",
      description: "See your hair loss future, risk, and timeline,completely private.",
      icon: Sparkles,
    },
    {
      title: "Get Your Action Plan",
      description:
        "Personalized, science-backed advice for your next steps (or peace of mind).",
      icon: ListChecks,
    },
  ];

  // Features (Why us)
  const features = [
    {
      title: "Cutting-Edge AI Analysis",
      description: "Our proprietary models predict future loss with 98%+ accuracy.",
      icon: Sparkles,
      stats: "98% accuracy",
    },
    {
      title: "Results in Seconds",
      description: "See your prediction in under 10 seconds. No waiting, no sales calls.",
      icon: Zap,
      stats: "<10 seconds",
    },
    {
      title: "Private & Secure",
      description: "Your photos are never stored or shared. You stay in control.",
      icon: Shield,
      stats: "100% confidential",
    },
  ];

  // Benefits (Value props)
  const benefits = [
    {
      icon: Target,
      title: "Personalized Analysis",
      description: "See exactly how you compare.",
    },
    {
      icon: Zap,
      title: "Lightning Fast",
      description: "Get answers instantly.",
    },
    {
      icon: Clock,
      title: "Zero Commitment",
      description: "No signup required. Free and private.",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-red-50 via-white to-white dark:from-dark-bg dark:via-dark-surface dark:to-dark-bg transition-colors">
      <div className="container mx-auto px-4 py-10 md:py-16">
        {/* --------------------------- HERO --------------------------- */}
        <section className="max-w-5xl mx-auto mb-20">
          <h1 className="text-4xl md:text-7xl font-bold leading-tight text-gray-900 dark:text-dark-text mb-6">
            Check Your <br />
            <span className="text-primary-600">Hair Loss Risk</span>
          </h1>
          <ul className="space-y-3 mb-8">
            {[
              "See how long you have",
              "Check your hair loss.",
              "No signup, totally private.",
            ].map((item, i) => (
              <li
                key={i}
                className="flex items-center text-lg md:text-xl text-gray-700 dark:text-dark-text-secondary"
              >
                <ChevronRight className="text-primary-700 mr-3" size={22} />
                {item}
              </li>
            ))}
          </ul>
          <div className="flex flex-col gap-4 max-w-md">
<CTAButton
  label="Free Hair Loss Check"
  onClick={goToStart}
  className="text-3xl"
/>            <p className="text-sm text-gray-500">
              No signup • 100% Free • Takes 60 seconds
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {benefits.map((b, i) => (
              <div
                key={i}
                className="rounded-2xl bg-white dark:bg-dark-surface p-6 shadow-sm border border-gray-100 dark:border-dark-border text-center"
              >
                <b.icon className="mx-auto mb-3 text-primary-600" size={26} />
                <h3 className="font-semibold mb-1">{b.title}</h3>
                <p className="text-sm text-gray-600 dark:text-dark-text-secondary">
                  {b.description}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* ---------------------- STATS & SOCIAL PROOF ---------------------- */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center mb-20">
          {[
            { label: "Users Helped", value: "7,000+" },
            { label: "Photos Analyzed", value: "330,000+" },
            { label: "Avg. Time", value: "30s" },
            { label: "Early Detection", value: "95%" },
          ].map((stat, i) => (
            <div key={i}>
              <div className="text-3xl font-bold text-black dark:text-white">
                {stat.value}
              </div>
              <div className="text-sm text-gray-500">{stat.label}</div>
            </div>
          ))}
        </section>

        {/* ---------------------- HOW IT WORKS ---------------------- */}
        <section className="max-w-6xl mx-auto mb-24">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            How It Works
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((step, i) => (
              <div
                key={i}
                className="bg-white dark:bg-dark-surface p-8 rounded-2xl shadow-sm hover:shadow-lg transition"
              >
                <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mb-5">
                  <step.icon className="text-primary-700" size={26} />
                </div>
                <h3 className="font-semibold text-lg mb-2">{step.title}</h3>
                <p className="text-gray-600 dark:text-dark-text-secondary">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
          <div className="mt-10 flex flex-col md:flex-row gap-4 justify-center">
            <CTAButton
              label="Try Your Free Preview"
              onClick={goToStart}
              variant="small"
            />
            <CTAButton
              label="See FAQ"
              onClick={goToFAQ}
              variant="secondary"
            />
          </div>
        </section>

        {/* ---------------------- FEATURES / WHY US ---------------------- */}
        <section className="max-w-6xl mx-auto mb-24">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            Why Choose WhenBald?
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((f, i) => (
              <div
                key={i}
                className="bg-white dark:bg-dark-surface p-8 rounded-2xl shadow-sm border border-gray-100 dark:border-dark-border"
              >
                <f.icon className="text-primary-600 mb-4" size={26} />
                <h3 className="font-semibold mb-2">{f.title}</h3>
                <p className="text-gray-600 dark:text-dark-text-secondary mb-3">
                  {f.description}
                </p>
                <div className="flex items-center gap-2 text-primary-700 font-medium">
                  <CheckCircle2 size={16} />
                  {f.stats}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-10 flex justify-center">
            <CTAButton
              label="Get Your Free Preview"
              onClick={goToStart}
              variant="small"
            />
          </div>
        </section>

        {/* ---------------------- TESTIMONIALS ---------------------- */}
        <section className="max-w-4xl mx-auto mb-24">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-8">
            Testimonials
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((t, i) => (
              <div
                key={i}
                className="bg-white dark:bg-dark-surface border border-gray-100 dark:border-dark-border rounded-2xl p-6 shadow-sm text-center flex flex-col"
              >
                <t.icon className="mx-auto text-primary-600 mb-4" size={28} />
                <div className="flex flex-col grow">
                  <p className="text-lg italic text-gray-700 dark:text-dark-text-secondary mb-4">
                    “{t.quote}”
                  </p>
                  <div className="flex items-center justify-center gap-2 text-primary-700 font-semibold mt-auto">
                    <Star size={16} className="text-yellow-500" />
                    <span>{t.name}</span>
                  </div>
                  <div className="text-xs text-gray-400 mt-1">{t.extra}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-center mt-10">
            <CTAButton
              label="Check With Friends"
              onClick={startShare}
              variant="primary"
            />
          </div>
          <p className="text-sm text-gray-500 text-center mt-4">
            Try it with your friends, see who gets the best result and share your peace of mind.
          </p>
        </section>

        {/* ---------------------- FINAL CTA ---------------------- */}
        <section className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Discover Your Hair’s Future?
          </h2>
          <p className="text-lg text-gray-600 dark:text-dark-text-secondary mb-8">
            Get a personalized hair loss preview, risk score, and action plan in 2 minutes. No signup, just real answers.
          </p>
          <div className="flex flex-col items-center gap-4">
            <CTAButton label="Get My Free Preview" onClick={goToStart} />
            <CTAButton
              label="Learn More"
              onClick={goToStart}
              variant="secondary"
            />
          </div>
        </section>
      </div>
    </div>
  );
};

export default Home;
